import { useState } from 'react';
import { Box, Container, Grid, Typography, Card, CardMedia, Dialog, IconButton } from '@mui/material';
import { Play, X } from 'lucide-react';

export function VideoGallery({ data }) {
  const primaryColor = data.primary_color || '#1e3a8a';
  const videos = Array.isArray(data.video_gallery) ? data.video_gallery : [];

  if (videos.length === 0) return null;

  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: 6 }}>
          <Box sx={{ display: 'inline-block', px: 2, py: 0.5, borderRadius: 4, backgroundColor: `${primaryColor}15`, color: primaryColor, mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700} letterSpacing="0.05em">
              VIDEO HIGHLIGHTS
            </Typography>
          </Box>
          <Typography variant="h2" fontWeight={800} color="#0f172a" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1 }}>
            Experience Our Campus
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Take a virtual tour and explore our state-of-the-art facilities and events.
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ justifyContent: 'center' }}>
          {videos.map((vid, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={vid.id || index}>
              <Card
                onClick={() => setActiveVideo(vid)}
                elevation={0}
                sx={{
                  borderRadius: 5,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  position: 'relative',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 25px 45px rgba(0,0,0,0.15)',
                    '& .play-btn': {
                      transform: 'scale(1.15)',
                      backgroundColor: primaryColor,
                    },
                  },
                }}
              >
                <Box sx={{ position: 'relative', height: { xs: 260, md: 320 } }}>
                  <CardMedia component="img" image={vid.thumbnail} alt={vid.title} sx={{ height: '100%', objectFit: 'cover' }} />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(15, 23, 42, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      className="play-btn"
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: primaryColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                      }}
                    >
                      <Play size={32} style={{ marginLeft: 4 }} />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      insetX: 0,
                      p: 3,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
                      color: '#ffffff',
                    }}
                  >
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: '1.1rem' }}>
                      {vid.title}
                    </Typography>
                    {vid.duration && (
                      <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>
                        Duration: {vid.duration}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Video Modal */}
      <Dialog
        open={Boolean(activeVideo)}
        onClose={() => setActiveVideo(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#000000', borderRadius: 4, overflow: 'hidden' },
        }}
      >
        <Box sx={{ position: 'relative', pt: '56.25%' }}>
          <IconButton
            onClick={() => setActiveVideo(null)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: '#ffffff',
              backgroundColor: 'rgba(0,0,0,0.6)',
              zIndex: 10,
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.9)' },
            }}
          >
            <X size={24} />
          </IconButton>
          {activeVideo && (
            <iframe
              src={activeVideo.youtube_url}
              title={activeVideo.title}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

export default VideoGallery;
